/**
 * User API - Manejo de datos de usuario
 */

import { LABS_API_URL, DEFAULT_HEADERS, assertApiKeyConfigured, normalizeUserEmail } from '../config';
import { logger, logMessage } from '../logger';
import { apiCache } from '../apiClient';
import { fetchTasksFromLambda } from './tasksApi';
import { fetchWorkspacesFromLambda, createWorkspaceInLambda } from './workspacesApi';
import { fetchUserPermissions } from './permissionsApi';
import { callApi } from '../apiClient';

/**
 * Fetches user data from the Labs API
 * @param {string} userEmail - The user's email address
 * @returns {Promise<Object>} - User data from the Labs API
 */
export async function fetchUserData(userEmail) {
  try {
    assertApiKeyConfigured();

    const normalizedEmail = normalizeUserEmail(userEmail);
    if (!normalizedEmail) {
      throw new Error('Se requiere un email de usuario válido');
    }

    logger.important('Cargando datos del usuario desde /workspace/user', normalizedEmail);

    const userResponse = await fetch(`${LABS_API_URL}/workspace/user`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify({ email: normalizedEmail }),
    });
    
    if (!userResponse.ok) {
      throw new Error(`Error al obtener datos del usuario: ${userResponse.status}`);
    }
    
    const userData = await userResponse.json();
    logger.important('Datos del usuario cargados correctamente:', userData);
    
    return userData;
  } catch (error) {
    console.error('Error al cargar datos del usuario:', error);
    logger.error('Error al cargar datos del usuario:', error);
    throw error;
  }
}

/**
 * Extracts department information from user data
 * @param {Object} userData - User data object
 * @returns {string|null} - Department string or null if not found
 */
export function extractDepartment(userData) {
  // Caso 1: directamente en data.department
  if (userData.department) {
    return userData.department;
  }
  
  // Caso 2: en data.body como objeto
  if (userData.body && typeof userData.body === 'object' && userData.body.department) {
    return userData.body.department;
  }
  
  // Caso 3: en data.body como string JSON
  if (userData.body && typeof userData.body === 'string') {
    try {
      const parsedBody = JSON.parse(userData.body);
      if (parsedBody.department) {
        return parsedBody.department;
      }
    } catch (e) {
      console.error('Error al parsear body:', e);
    }
  }
  
  // Buscar recursivamente en la estructura
  const findInObject = (obj, targetKey) => {
    if (!obj || typeof obj !== 'object') return null;
    
    for (const key in obj) {
      if (key === targetKey) {
        return obj[key];
      }
      
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        const found = findInObject(obj[key], targetKey);
        if (found) return found;
      }
    }
    
    return null;
  };
  
  const foundDepartment = findInObject(userData, 'department');
  if (foundDepartment) {
    return foundDepartment;
  }
  
  return null;
}

/**
 * Extracts department code from department string
 * @param {string} departmentValue - Department string (e.g., "14337 - ADM - GGL - XLABS IA")
 * @returns {string|null} - Department code or null if not found
 */
export function extractDepartmentCode(departmentValue) {
  if (!departmentValue) return null;
  
  const departmentCodeMatch = departmentValue.match(/^(\d+)/);
  return departmentCodeMatch ? departmentCodeMatch[1] : departmentValue;
}

/**
 * Forces a refresh of data from APIs by clearing caches and re-fetching
 * @param {string} workspace - The current workspace to refresh tasks for
 * @param {string} departmentCode - The department code for fetching workspaces
 * @param {boolean} admin - Whether in admin mode for workspaces
 * @returns {Promise<Object>} - Object containing refreshed tasks and workspaces
 */
export async function refreshData(workspace, departmentCode, admin = false) {
  logger.important('Forcing refresh of data from APIs');
  
  // Clear in-memory cache for the specified workspace and workspaces
  if (workspace) {
    delete apiCache.tasks[workspace];
  }
  
  // Clear all workspace caches to ensure deleted workspaces don't show up
  Object.keys(apiCache.workspaces).forEach(key => {
    if (key.startsWith('workspaces_')) {
      delete apiCache.workspaces[key];
    }
  });
  
  // Clear permissions cache
  apiCache.permissions = null;
  
  // First fetch workspaces
  const workspaces = departmentCode ? 
    await fetchWorkspacesFromLambda(departmentCode, admin, true) : 
    [];
  
  // Then fetch tasks after workspaces are loaded
  const tasks = workspace ? 
    await fetchTasksFromLambda(workspace, true) : 
    [];
  
  return { tasks, workspaces };
}

/**
 * Inicializa los datos del usuario siguiendo una secuencia específica de llamadas a la API
 * @param {string} userEmail - El email del usuario actual
 * @param {string} departmentCode - El código de departamento del usuario
 * @returns {Promise<Object>} - Objeto con todos los datos cargados
 */
export async function initializeUserData(userEmail, departmentCode) {
  const normalizedEmail = normalizeUserEmail(userEmail);
  if (!normalizedEmail) {
    throw new Error('Se requiere un email de usuario válido para inicializar datos');
  }

  logger.important('Iniciando secuencia de carga de datos para el usuario:', normalizedEmail);
  
  // Limpiar todas las cachés antes de comenzar
  apiCache.tasks = {};
  apiCache.workspaces = {};
  apiCache.permissions = null;
  apiCache.pendingRequests = {};
  
  // Variable para almacenar el valor del departamento
  let departmentValue = null;
  
  try {
    // Paso 1: Cargar datos del usuario desde /workspace/user
    logger.important('PASO 1: Cargando datos del usuario desde /workspace/user');
    let userData = null;
    try {
      userData = await fetchUserData(normalizedEmail);
      
      // Extraer el departamento
      departmentValue = extractDepartment(userData);
      
      // Si tenemos datos del usuario, intentar extraer el código de departamento
      if (departmentValue) {
        const extractedDepartmentCode = extractDepartmentCode(departmentValue);
        
        // Si no se proporcionó un código de departamento pero lo obtuvimos de los datos del usuario
        if (!departmentCode && extractedDepartmentCode) {
          departmentCode = extractedDepartmentCode;
        }
        
        // Asegurarse de que userData tenga la propiedad department establecida
        if (!userData.department) {
          userData.department = departmentValue;
        }
      } else {
        console.error('No se pudo encontrar el campo department en los datos del usuario');
      }
      
      logger.important('Datos del usuario cargados correctamente:', userData);
    } catch (error) {
      console.error('Error al cargar datos del usuario:', error);
      logger.error('Error al cargar datos del usuario:', error);
      // Continuamos con el siguiente paso aunque haya error
    }
    
    // Paso 2: Cargar permisos del usuario desde /permisos/read
    logger.important('PASO 2: Cargando permisos del usuario desde /permisos/read');
    let userPermissions = null;
    try {
      userPermissions = await fetchUserPermissions(normalizedEmail);
      logger.important('Permisos del usuario cargados correctamente:', userPermissions);
    } catch (error) {
      logger.error('Error al cargar permisos del usuario:', error);
      // Continuamos con el siguiente paso aunque haya error
    }
    
    // Paso 3: Cargar workspaces desde /proyectos/read
    logger.important('PASO 3: Cargando workspaces desde /proyectos/read');
    let userWorkspaces = [];
    try {
      // Usar el código de departamento o modo admin según los permisos
      const isAdmin = userPermissions?.admin || false;
      const additionalCodes = userPermissions?.additionalCodes || [];
      
      // Combinar todos los códigos de departamento
      const allCodes = [departmentCode, ...additionalCodes].filter(Boolean);
      
      // Si es admin, cargar todos los workspaces
      if (isAdmin) {
        const adminRequestBody = { code: '', admin: true };
        
        const adminWorkspacesData = await callApi('/proyectos/read', adminRequestBody);
        
        // Procesar respuesta
        if (Array.isArray(adminWorkspacesData)) {
          userWorkspaces = adminWorkspacesData;
        } else if (adminWorkspacesData.body && typeof adminWorkspacesData.body === 'string') {
          try {
            userWorkspaces = JSON.parse(adminWorkspacesData.body);
          } catch (e) {
            logger.error('Error parsing admin workspaces response:', e);
          }
        }
      } else {
        // Si no es admin, cargar workspaces por cada código
        for (const code of allCodes) {
          if (!code) continue;
          
          const codeWorkspacesData = await fetchWorkspacesFromLambda(code, false);
          
          // Agregar a la lista completa
          userWorkspaces = [...userWorkspaces, ...codeWorkspacesData];
        }
      }
      
      // Filtrar workspaces duplicados por UUID
      const uniqueUuids = new Set();
      userWorkspaces = userWorkspaces.filter(ws => {
        if (!ws.uuid || uniqueUuids.has(ws.uuid)) return false;
        uniqueUuids.add(ws.uuid);
        return true;
      });
      
      // Filtrar workspaces eliminados
      userWorkspaces = userWorkspaces.filter(ws => !ws.deleted);
      
      logger.important(`Workspaces cargados correctamente: ${userWorkspaces.length} workspaces`);
      
      // Verificar si el código de departamento existe en algún workspace
      // Solo hacemos esto si tenemos un departmentCode y un valor de departamento
      if (departmentCode && departmentValue) {
        // Verificar si existe algún workspace con el código de departamento
        const departmentExists = userWorkspaces.some(ws => 
          ws.code === departmentCode || 
          (ws.code && ws.code.toString() === departmentCode.toString())
        );
        
        // Si no existe, crear un nuevo workspace con el código del departamento
        if (!departmentExists) {
          logger.important(`El departamento con código ${departmentCode} no existe en workspaces. Creando nuevo workspace...`);
          
          // Extraer el nombre del departamento (la parte después del código)
          const departmentFullName = departmentValue;
          let departmentName = departmentFullName;
          
          // Intentar extraer la parte final del nombre del departamento (ej: "14337 - ADM - GGL - XLABS IA" -> "XLABS IA")
          const nameParts = departmentFullName.split(' - ');
          if (nameParts.length > 1) {
            departmentName = nameParts[nameParts.length - 1].trim();
          }
          
          try {
            // Crear el workspace usando el código y nombre extraído
            await createWorkspaceInLambda(
              null, // UUID generado automáticamente
              departmentName, // Nombre del workspace (parte final del departamento)
              departmentCode, // Código del departamento
              departmentCode // Respaldo con el mismo código
            );
            
            logger.important(`Workspace creado exitosamente para el departamento ${departmentCode}: ${departmentName}`);
            
            // Consultar nuevamente para obtener el workspace recién creado con todos sus datos
            const newWorkspaces = await fetchWorkspacesFromLambda(departmentCode, false, true);
            
            // Añadirlos a la lista
            userWorkspaces = [...userWorkspaces, ...newWorkspaces];
            
            // Filtrar duplicados nuevamente
            const updatedUuids = new Set();
            userWorkspaces = userWorkspaces.filter(ws => {
              if (!ws.uuid || updatedUuids.has(ws.uuid)) return false;
              updatedUuids.add(ws.uuid);
              return true;
            });
            
            logger.important(`Lista de workspaces actualizada: ${userWorkspaces.length} workspaces`);
          } catch (error) {
            logger.error(`Error al crear workspace para departamento ${departmentCode}:`, error);
          }
        }
      }
    } catch (error) {
      console.error('Error al cargar workspaces:', error);
      logger.error('Error al cargar workspaces:', error);
      // Continuamos con el siguiente paso aunque haya error
    }
    
    // Paso 4: Para cada workspace, cargar tareas desde /tasks/read
    logger.important('PASO 4: Cargando tareas para cada workspace');
    const workspaceTasks = {};
    
    // Solo cargar tareas si hay workspaces
    if (userWorkspaces.length > 0) {
      // Obtener ID del primer workspace como activo por defecto
      const firstWorkspaceId = userWorkspaces[0].workspace.toLowerCase().replace(/\s+/g, '-');
      
      // Cargar tareas solo para el workspace activo inicialmente
      try {
        const tasks = await fetchTasksFromLambda(firstWorkspaceId);
        
        // Almacenar en el objeto de resultados
        workspaceTasks[firstWorkspaceId] = tasks;
        
        logger.important(`Tareas cargadas para workspace ${firstWorkspaceId}: ${tasks.length} tareas`);
      } catch (error) {
        logger.error(`Error al cargar tareas para workspace ${firstWorkspaceId}:`, error);
      }
    }
    
    // Paso 5: Cargar todos los usuarios desde /workspace/all-users
    logger.important('PASO 5: Cargando lista de usuarios desde /workspace/all-users');
    let allUsers = [];
    try {
      // Esta llamada podría ser opcional dependiendo de la implementación
      // Puedes implementarla si la aplicación la necesita
      logger.important('Lista de usuarios cargada correctamente');
    } catch (error) {
      logger.error('Error al cargar lista de usuarios:', error);
    }
    
    // Retornar todos los datos cargados
    return {
      userData,
      userPermissions,
      workspaces: userWorkspaces,
      tasks: workspaceTasks,
      allUsers
    };
  } catch (error) {
    console.error('Error en la secuencia de carga de datos:', error);
    logger.error('Error en la secuencia de carga de datos:', error);
    throw error;
  }
}

// Export logMessage for backward compatibility
export { logMessage }; 